import { useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLoader, useTimeout, usePersonalLoan, usePersonalLoanRouteFsm } from 'shared/hooks';
import { Button as ButtonBase, Width } from '@figure/frontlib-gui';
import { PageHeader } from 'shared/components/Typography';
import { Typography } from '@figure/frontlib-gui/lib/Typography';

const StyledContainer = styled.div`
  button {
    margin-top: 30px;
  }

  @media print, screen and (min-width: ${Width.MD}px) {
    h3 {
      margin-top: 30px;
    }
  }
`;

const Heading = styled.h2`
  font-weight: 600;
  font-size: 1.5rem;
`;
const P = styled.p`
  font-weight: 500;
  line-height: 1.5;
`;

const Button = styled(ButtonBase)`
  margin-bottom: 24px;
`;

const Legal = styled(Typography.SmallBodyGrey)`
  text-align: center;
`;

const DocumentIntro = () => {
  const { personalLoan } = usePersonalLoan();
  const statusRef = useRef('');
  const history = useHistory();
  const { uuid: personalLoanUuid } = useParams();
  const runRouter = usePersonalLoanRouteFsm();
  const { t } = useTranslation();
  const companySingleName = t('companySingleName');

  const uwSubDecision = personalLoan.status?.uwSubDecision ?? '';

  useLoader('Generating signing documents', !personalLoan.status?.disclosurelistClosingReady);
  useTimeout(() => {
    if (!personalLoan.status?.disclosurelistClosingReady)
      history.replace(`/applications/${personalLoanUuid}/review-app/signdocs`);
  }, 20000);

  useEffect(() => {
    if (!statusRef.current && uwSubDecision === 'VERIFIED_OFFER') {
      statusRef.current = uwSubDecision;
      runRouter();
    }
  }, [uwSubDecision, runRouter]);

  if (!personalLoan.status?.disclosurelistClosingReady) return null;

  const handleSubmit = () => history.push(`/applications/${personalLoanUuid}/signing/sign`);

  return (
    <StyledContainer>
      <PageHeader>You’re approved, subject to the following closing conditions!</PageHeader>
      <Heading>Review and sign your loan documents</Heading>
      <P>
        Now that your application is complete, it is time to review the disclosures and sign your
        loan documents. Final documents will be available on your dashboard.
      </P>

      {personalLoan?.status?.bypassVoeReview !== 'BYPASS' && (
        <>
          <Heading>Verify your Employment</Heading>
          <P>
            If you indicated that your income source is employment, and we have not been able to
            verify your employment through our automated process, a {companySingleName} Customer
            Success Associate will contact you to obtain your employer's contact information.{' '}
            {companySingleName} will then verify your employment with your employer. If we're unable
            to verify your employment, we will not be able to fund your loan.
          </P>
        </>
      )}

      {!personalLoan?.status?.bypassKycReview && (
        <>
          <Heading>Talk to a {companySingleName} Customer Success Associate</Heading>
          <P>
            Connect with a {companySingleName} Customer Success Associate (CSA) to confirm some
            details and ask any questions you may have. You will be asked to confirm your name and
            date of birth and display the ID you submitted for identity verification during this
            video call.
          </P>
        </>
      )}

      <br />

      <Heading>
        Once you progress through these steps, your loan process will be complete and you will
        automatically receive your funds on the next business day.
      </Heading>

      <Button center primary large onClick={handleSubmit} data-test-id="approve-button">
        Continue
      </Button>

      <Legal>You have 14 calendar days to finish your loan process.</Legal>
    </StyledContainer>
  );
};

export default DocumentIntro;
